import { AxiosError } from "axios";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import reduce from "lodash/reduce";
import { getRemappedErrors } from "PFApp/booking/components/booking_form/get_remapped_errors";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBooking } from "PFCore/hooks/queries";
import { Dispatch, SetStateAction, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useBookingActivityContext } from "../../../parts/providers/booking_activity_context_provider";
import { GroupBookingsQuestionsOption } from "../../booking_questions/group_bookings_questions.types";
import {
  BookingFormData,
  BookingFormDataCreateMode,
  BookingFormDataEditOrCloneMode,
  BookingFormMode
} from "../booking_form.types";
import {
  clearKnownErrors,
  getSubmitErrorMessageTranslationKey,
  getSubmitSuccessMessageTranslationKey,
  setKnownErrors
} from "../booking_form.utils";
import { BookingFormValues } from "../booking_form_provider";
import { BookingType } from "../booking_type";
import { isGroupOption } from "../workforce_member_select/workforce_member_select.utils";
import { usePostSubmitActions } from "./use_post_submit_actions";
import { usePreSubmitActions } from "./use_pre_submit_actions";
import { useSubmitUpdate } from "./use_submit_update";

export type BookingResponse = {
  id: number;
};
export type OnSuccessData = {
  profileId: number | undefined;
  activityId: number | undefined;
  shortlistId: number | undefined;
  parentActivityIds: number[];
  mode: BookingFormMode;
  bookingType: BookingType;
};

export type UseHandleSubmit = {
  initialData: BookingFormData;
  mode: BookingFormMode;
  onSuccess?: (data: OnSuccessData) => void | Promise<any>;
  onError: () => void;
  formMethods: UseFormReturn<BookingFormValues>;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
  onPreSubmitAction?: (shortlistId: number | undefined) => Promise<void>;
};

type UseHandleSubmitReturn = {
  handleSubmit: (values: BookingFormValues) => Promise<void>;
  isLoading: boolean;
};

export const useHandleSubmit = ({
  initialData,
  mode,
  onSuccess,
  onError,
  formMethods,
  setLoadingState,
  onPreSubmitAction
}: UseHandleSubmit): UseHandleSubmitReturn => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("bookingModule");
  const { parentActivity } = useBookingActivityContext();

  const {
    setError,
    clearErrors,
    formState: { dirtyFields }
  } = formMethods;

  const bookingId =
    mode === BookingFormMode.Create ? undefined : (initialData as BookingFormDataEditOrCloneMode).bookingId;
  const { data: booking } = useBooking(bookingId, { enabled: !!bookingId });

  const submitUpdate = useSubmitUpdate({ mode, initialBooking: booking });

  const [isLoading, setIsLoading] = useState(false);
  const { getSingleErrorByType } = useResponseErrors();

  const shortlistId =
    mode === BookingFormMode.Create ? (initialData as BookingFormDataCreateMode)?.shortlistId : undefined;
  const bookingGroupId = booking?.booking_group_id;

  const { preSubmitActions } = usePreSubmitActions({ initialData, mode, setLoadingState, onPreSubmitAction });
  const { postSubmitActions } = usePostSubmitActions({ mode, shortlistId, bookingGroupId });

  const handleSubmit = async (values: BookingFormValues) => {
    const { bookings, workforceMember, activityId, updateGroupOptionSelected, bookingType } = values;
    if (isEmpty(bookings)) {
      return;
    }
    const dirtyBookingsIndexes = reduce(
      get(dirtyFields, "bookings") ?? [],
      (acc: number[], data, index) => {
        if (data) {
          acc.push(index);
        }
        return acc;
      },
      []
    );

    clearKnownErrors(mode, bookings, clearErrors, "submit");
    setIsLoading(true);

    try {
      const shortlistId = await preSubmitActions(values);
      const bookingsResponse = await submitUpdate(values, dirtyBookingsIndexes);
      postSubmitActions(bookingsResponse, values);
      await onSuccess?.({
        profileId: isGroupOption(workforceMember) ? undefined : workforceMember?.id,
        activityId,
        parentActivityIds: parentActivity?.id ? [parentActivity.id] : [],
        mode,
        bookingType,
        shortlistId
      });
      const isAffectingGroup =
        isGroupOption(workforceMember) ||
        (!isNil(booking?.booking_group_id) &&
          updateGroupOptionSelected === GroupBookingsQuestionsOption.GroupAll);
      const { actionId, messageKey } = getSubmitSuccessMessageTranslationKey(mode, isAffectingGroup);
      showGrowl({
        id: actionId,
        kind: "success",
        message: t(messageKey),
        hidePreviousByTypeAndId: true,
        ttl: 5000
      });
    } catch (error: any) {
      const response = error?.response;
      const responseErrors = getSingleErrorByType(response) || {};
      if (Object.keys(responseErrors).length > 0) {
        const errorMessage = responseErrors.base ?? responseErrors.search_id;
        errorMessage && showGrowl({ kind: "error", message: errorMessage });
        const errors = getRemappedErrors(error as AxiosError, mode, responseErrors, bookings);
        setKnownErrors(mode, bookings, errors, setError, clearErrors, "submit");
        return;
      }
      showGrowl({
        kind: "error",
        message: t(getSubmitErrorMessageTranslationKey(mode))
      });
      onError?.();
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleSubmit,
    isLoading
  };
};
